import { ChangeEvent, FC, useRef, useState } from 'react';
import { Modal, ModalFooter, Button } from '@/components/ui';
import { Upload, FileText, AlertCircle, CheckCircle2, Loader2, X, Download } from 'lucide-react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';

interface LeadImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    userId?: string;
}

export const LeadImportModal: FC<LeadImportModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    userId,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                setError('Please select a valid CSV file');
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setError(null);
            setResult(null);
        }
    };

    const handleUpload = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);
        setResult(null);

        const formData = new FormData();
        formData.append('file', file);
        if (userId) {
            formData.append('userId', userId);
        }

        try {
            const response = await api.post('/mxm/leads/import', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            setResult(response.data);
            onSuccess();
        } catch (err: unknown) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setError(null);
        setResult(null);
        onClose();
    };

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Import Leads from CSV"
            size="md"
        >
            <div className="space-y-6 py-2">
                {!result ? (
                    <>
                        <div className="text-sm text-gray-600 flex justify-between items-center">
                            <span>
                                Upload a CSV file containing an <span className="font-semibold">email</span> column.
                                Only rows with valid email addresses will be imported.
                            </span>
                            <a
                                href="/leads_sample.csv"
                                download
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 shrink-0 ml-4"
                            >
                                <Download className="w-3 h-3" /> Sample CSV
                            </a>
                        </div>

                        <div
                            onClick={triggerFileInput}
                            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${file ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                                }`}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept=".csv"
                                className="hidden"
                            />

                            {file ? (
                                <div className="flex flex-col items-center">
                                    <FileText className="w-10 h-10 text-blue-500 mb-2" />
                                    <span className="text-sm font-medium text-gray-900">{file.name}</span>
                                    <span className="text-xs text-gray-500 mt-1">
                                        {(file.size / 1024).toFixed(2)} KB
                                    </span>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFile(null);
                                        }}
                                        className="mt-3 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                                    >
                                        <X className="w-3 h-3" /> Remove
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mb-3">
                                        <Upload className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">Click to upload or drag and drop</span>
                                    <span className="text-xs text-gray-500 mt-1">CSV files only</span>
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-sm">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center py-4 text-center">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-10 h-10 text-green-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Import Complete</h3>
                        <p className="text-sm text-gray-600 mb-6">
                            Your leads have been processed successfully.
                        </p>

                        <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                                <div className="text-2xl font-bold text-blue-600">{result.imported}</div>
                                <div className="text-xs text-gray-500 uppercase font-semibold">Imported</div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                                <div className="text-2xl font-bold text-gray-600">{result.skipped}</div>
                                <div className="text-xs text-gray-500 uppercase font-semibold">Skipped</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <ModalFooter>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={loading}
                >
                    {result ? 'Close' : 'Cancel'}
                </Button>
                {!result && (
                    <Button
                        onClick={handleUpload}
                        disabled={!file || loading}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-2 min-w-[100px]"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Importing...
                            </>
                        ) : (
                            'Start Import'
                        )}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};
